import { FunctionComponent, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";

import { FeatureLayout } from "../../components/Shared/Layouts/FeatureLayout/FeatureLayout";
import { translationKeys } from "../../models/app-translation-keys";
import { AllLanguageOptions } from "../../models/constants/app-language-options.const";
import { ILanguageOption } from "../../models/interfaces";
import { useStore } from "../../stores/StoreContext";
import { TranslationContentFile } from "./TranslationContentFile/TranslationContentFile";
import { TranslationContentText } from "./TranslationContentText/TranslationContentText";
import { IGlossaryFileSupportExtended } from "./TranslationHeader/GlossarySelection/GlossarySelection.types";
import { TranslationHeader, TranslationType } from "./TranslationHeader/TranslationHeader";

import "./TranslationPage.scss";

import { useTranslation } from "react-i18next";

import { Dropdown } from "primereact/dropdown";

import { TranslatorProviderType } from "../../../@api";

const RawTranslationPage: FunctionComponent = () => {
  const { t } = useTranslation();
  const { translationStore, userStore } = useStore();
  const [selectedGlossary, setSelectedGlossary] = useState<IGlossaryFileSupportExtended | null>(null);
  const [translationType, setTranslationType] = useState<TranslationType>(TranslationType.Text);

  const translationTranslationKeys = translationKeys.feature.translation;
  const translatorProviderKeys = translationTranslationKeys.translatorProvider;

  const [sourceLanguage, setSourceLanguage] = useState<ILanguageOption>(
    AllLanguageOptions.find(lang => lang.code === userStore.currentLanguage.code) || AllLanguageOptions[0],
  );
  const [targetLanguage, setTargetLanguage] = useState<ILanguageOption>(
    AllLanguageOptions.find(lang =>
      userStore.currentLanguage.code === "de" ? lang.code === "en" : lang.code === "de",
    ) || AllLanguageOptions[0],
  );

  const [selectedProviderType, setSelectedProviderType] = useState<TranslatorProviderType>(
    TranslatorProviderType.AzureTranslator,
  );

  const providerTypeOptions = useMemo(
    () => [
      {
        label: t(translatorProviderKeys.azureTranslator),
        value: TranslatorProviderType.AzureTranslator,
      },
      {
        label: t(translatorProviderKeys.llmTranslator),
        value: TranslatorProviderType.LlmTranslator,
      },
    ],
    [userStore.currentLanguage],
  );

  const providerSelectionMemo = useMemo(
    () => (
      <div className="translator-provider-selection">
        <Dropdown
          placeholder={t(translatorProviderKeys.label)}
          value={selectedProviderType}
          options={providerTypeOptions}
          onChange={e => setSelectedProviderType(e.value)}
          className="translator-provider-dropdown"
        />
      </div>
    ),
    [userStore.currentLanguage, selectedProviderType, providerTypeOptions],
  );

  const showTabByTranslationType = (type: TranslationType) => {
    return translationType === type ? "block" : "none";
  };

  return (
    <FeatureLayout
      titleKey={translationTranslationKeys.title}
      subTitleKey={translationTranslationKeys.subTitle}
      descriptionKey={translationTranslationKeys.description}
      hasPendingChanges={translationStore.isAnyFileProgressing || translationStore.isTextProcessing}
    >
      <div className="translation-content">
        <TranslationHeader
          sourceLanguage={sourceLanguage}
          targetLanguage={targetLanguage}
          setSourceLanguage={setSourceLanguage}
          setTargetLanguage={setTargetLanguage}
          onTranslationTypeChange={setTranslationType}
          onGlossaryChange={setSelectedGlossary}
        />

        {translationType === TranslationType.Text && !selectedGlossary && providerSelectionMemo}

        <div style={{ display: showTabByTranslationType(TranslationType.File) }}>
          <TranslationContentFile
            sourceLanguage={sourceLanguage}
            targetLanguage={targetLanguage}
            selectedGlossary={selectedGlossary}
          />
        </div>

        <div style={{ display: showTabByTranslationType(TranslationType.Text) }}>
          <TranslationContentText
            sourceLanguage={sourceLanguage}
            targetLanguage={targetLanguage}
            selectedGlossary={selectedGlossary}
            selectedProviderType={selectedProviderType}
          />
        </div>
      </div>
    </FeatureLayout>
  );
};

export const TranslationPage = observer(RawTranslationPage);
